import axios, { type AxiosInstance } from 'axios'
import { useAuthStore } from '../stores/auth'

// API base URL - can be configured via environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const authStore = useAuthStore()
  if (authStore.accessToken) {
    config.headers.Authorization = `Bearer ${authStore.accessToken}`
  }
  return config
})

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const authStore = useAuthStore()

    // If 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry && authStore.refreshToken) {
      originalRequest._retry = true

      try {
        await authStore.refreshTokens()
        originalRequest.headers.Authorization = `Bearer ${authStore.accessToken}`
        return api(originalRequest)
      } catch {
        authStore.logout()
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

export default api

// API types
export interface User {
  id: string
  username: string
  display_name: string
  avatar_url: string
}

export interface AuthResult {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
  user: User
}

export interface Product {
  sku: string
  game_id: string
  display_name: string
  description: string
  category: string
  default_cost: number
  default_cooldown: number
  active: boolean
}

export interface RewardMapping {
  id: string
  user_id: string
  twitch_reward_id: string
  product_sku: string
  twitch_reward_title: string
  cost: number
  cooldown: number
  enabled: boolean
  product?: Product
}

export interface Server {
  id: string
  name: string
  game_id: string
  owner_id: string
  last_seen: string | null
}

export interface ServerMember {
  user_id: string
  display_name: string
  username: string
  avatar_url: string
  is_owner: boolean
}

export interface GlobalIdentity {
  id: string
  user_id: string
  platform: string
  platform_id: string
  platform_username: string
}

export interface GameIdentity {
  id: string
  user_id: string
  game_id: string
  identity_type: string
  identity_value: string
}

export interface StreamerProfile {
  id: string
  username: string
  display_name: string
  avatar_url: string
  global_identities: GlobalIdentity[]
  game_identities: GameIdentity[]
  reward_mappings: RewardMapping[]
  servers: Server[]
}
